import type { ReactNode } from 'react';

// Piezas del manual de uso integrado (pedido de Fernando 14/07/2026): cada
// módulo tiene su página de ayuda con lenguaje simple y capturas de pantalla,
// accesible desde el "?" del header. Las imágenes viven en public/manual/.

type Accent = 'primario' | 'verde' | 'azul';

const ACENTO_TEXTO: Record<Accent, string> = {
  primario: 'text-primario-400',
  verde: 'text-verde-400',
  azul: 'text-azul-400',
};
const ACENTO_FONDO: Record<Accent, string> = {
  primario: 'bg-primario-100 text-primario-700',
  verde: 'bg-verde-100 text-verde-600',
  azul: 'bg-azul-100 text-azul-600',
};

export interface SeccionDef {
  id: string;
  titulo: string;
  contenido: ReactNode;
}

/** Página completa del manual: intro + índice con anclas + secciones. */
export function PaginaAyuda({
  titulo,
  intro,
  accent,
  secciones,
}: {
  titulo: string;
  intro: string;
  accent: Accent;
  secciones: SeccionDef[];
}): ReactNode {
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header>
        <h2 className="text-xl font-bold text-neutro-800">{titulo}</h2>
        <p className="mt-1 text-sm text-neutro-500">{intro}</p>
      </header>

      <nav className="rounded-2xl border border-contorno-var bg-white p-4">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-neutro-500">¿Qué querés hacer?</p>
        <ul className="space-y-1">
          {secciones.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className={`text-sm font-semibold ${ACENTO_TEXTO[accent]} hover:underline`}>
                {s.titulo}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {secciones.map((s, i) => (
        <section key={s.id} id={s.id} className="scroll-mt-20 rounded-2xl border border-contorno-var bg-white p-5">
          <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-neutro-800">
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${ACENTO_FONDO[accent]}`}>
              {i + 1}
            </span>
            {s.titulo}
          </h3>
          <div className="space-y-3 text-sm leading-relaxed text-neutro-700">{s.contenido}</div>
        </section>
      ))}

      <p className="pb-4 text-center text-xs text-neutro-400">
        ¿Algo no funciona como dice acá? Avisale al administrador.
      </p>
    </div>
  );
}

/** Captura de pantalla con pie de foto. src relativo a /manual/. */
export function Captura({ src, alt, pie }: { src: string; alt: string; pie?: string }): ReactNode {
  return (
    <figure className="my-3">
      <img
        src={`/manual/${src}`}
        alt={alt}
        loading="lazy"
        className="w-full max-w-sm rounded-xl border border-contorno-var shadow-sm"
      />
      {pie && <figcaption className="mt-1.5 text-xs text-neutro-400">{pie}</figcaption>}
    </figure>
  );
}

/** Lista de pasos numerados ("1. Tocá +", "2. Elegí la fecha…"). */
export function Pasos({ items }: { items: ReactNode[] }): ReactNode {
  return (
    <ol className="space-y-1.5">
      {items.map((p, i) => (
        <li key={i} className="flex gap-2">
          <span className="shrink-0 font-bold text-neutro-400">{i + 1}.</span>
          <span>{p}</span>
        </li>
      ))}
    </ol>
  );
}
